// @ts-nocheck
import {
  Modal,
  Box,
  Typography,
  Button,
  Grid,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  closeManageApprovalModalAction,
  getContractorDetailsForManageApprovalsAction,
  updateManageApprovalDataAction,
} from "../../store/actions/config/contractorLink";
import LoadingOverlay from "../Custom/LoadingOverlay";
import PropTypes from "prop-types";
import { useEffect, useMemo, useCallback, memo, useState } from "react";
import {
  apiErrorMessage,
  fetchOnboardingStatus,
  sendOnboarding,
  setVerified,
} from "../../api/onboarding";
import { AGREEMENT_CONTRACTOR_TYPES } from "../../utils/constants/configs";
import { CustomSwitch } from "../Custom/Switch";
import { alertDialogAciton } from "../../store/actions/auth";
import {
  gustoApproveToggleContractorAction,
  toggleAdpStatusAction,
  toggleContractorStatusAction,
  toggleOnboardingStatusAction,
} from "../../store/actions/contractor";
import { CONTRACTOR_TYPE_3RD_PARTY } from "../../utils/constants/nmaes";
import { getCssVariableHandler } from "../../utils/styles/getCssVariables";
import { Close } from "@mui/icons-material";
import { CustomIconButtonForSheet } from "../Custom/CustomIconButton";
import { isUserRole } from "../../utils/methods/storeFunc";
import { USER_ROLES } from "../../staticData/userRoles";
import { contractorTypesFrontend } from "../../pages/contractors/components/AddContractorForms/formSchemas/addContractorStep1Schema";
import { hasAssignedContractorType } from "../../utils/methods/contractors/contractorUtils";

// NOTE: style for modal
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  boxShadow: 24,
  borderRadius: "16px",
  padding: "24px",
  border: "2px solid #D175B6",
  backgroundColor: "#000",
  zIndex: 9,
  display: "flex",
  flexDirection: "column",
  gap: "1.5rem",
  maxWidth: "90vw",
  maxHeight: "95vh",
  width: "480px",
  minHeight: "500px",

  "@media (max-width: 900px)": {
    width: "95vw",
  },

  "@media (max-width: 600px)": {
    width: "97vw",
    maxWidth: "97vw",
    maxHeight: "96vh",
    padding: "16px",
    gap: "1rem",
  },
};

const APPROVAL_TYPES = {
  TOGGLE: "TOGGLE",
  DROPDOWN: "DROPDOWN",
};

// Memoized approval item component
const ApprovalItem = memo(
  ({
    title,
    valueKey,
    checked,
    onClick,
    loading,
    isLast,
    readOnly,
    disabled,
  }) => {
    if (disabled) {
      return null;
    }
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 2,
          borderBottom: isLast ? "none" : "1px solid rgba(255, 255, 255, 0.08)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.02)",
          },
          transition: "background-color 0.2s ease",
          px: 1,
          borderRadius: "8px",
        }}
      >
        <Typography
          sx={{
            fontSize: "1rem",
            fontWeight: 400,
            color: "rgba(255, 255, 255, 0.9)",
          }}
        >
          {title}
        </Typography>
        {readOnly ? (
          <Typography>{checked ? "Approved" : "Unapproved"}</Typography>
        ) : loading ? (
          <div style={{ marginRight: "1rem" }} className="status-loader" />
        ) : (
          <CustomSwitch checked={checked} onClick={onClick} />
        )}
      </Box>
    );
  },
);

ApprovalItem.displayName = "ApprovalItem";

ApprovalItem.propTypes = {
  title: PropTypes.string.isRequired,
  valueKey: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  isLast: PropTypes.bool,
  readOnly: PropTypes.bool,
};

export const ManageApprovals = () => {
  const { manageApprovals } = useSelector((state) => state?.contractorLink);
  const { isLoading, open, contractorId, contractorDetails } = manageApprovals;
  const auth = useSelector((state) => state?.auth);
  const {
    statusLoader: enableLoading,
    gLoading: hrLoading,
    aLoading: verifyLoading,
    adpLoading: payrollLoading,
  } = useSelector((state) => state?.contractor);
  const dispatch = useDispatch();

  // Onboarding (2026-10-08): checklist, Verified note and "Send onboarding".
  const [onboarding, setOnboarding] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [verifyNote, setVerifyNote] = useState("");
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [onboardingMessage, setOnboardingMessage] = useState("");

  const loadOnboarding = useCallback(async () => {
    if (!contractorId) return;
    try {
      setOnboarding(await fetchOnboardingStatus(contractorId));
    } catch {
      setOnboarding(null);
    }
  }, [contractorId]);

  useEffect(() => {
    setOnboardingMessage("");
    if (open) loadOnboarding();
  }, [open, loadOnboarding]);

  const saveVerified = useCallback(
    async (verified, note = "") => {
      setVerifyBusy(true);
      try {
        await setVerified(contractorId, verified, note);
        dispatch(
          updateManageApprovalDataAction({
            payload: { key: "isApproved", value: verified },
          }),
        );
        setNoteOpen(false);
        setVerifyNote("");
        await loadOnboarding();
      } catch (e) {
        dispatch(
          alertDialogAciton({
            open: true,
            title: "Error",
            message: apiErrorMessage(e),
          }),
        );
      } finally {
        setVerifyBusy(false);
      }
    },
    [contractorId, dispatch, loadOnboarding],
  );

  const handleSendOnboarding = useCallback(async () => {
    setSendBusy(true);
    try {
      const res = await sendOnboarding(contractorId);
      setOnboardingMessage(res?.message ?? "Onboarding sent.");
      await loadOnboarding();
    } catch (e) {
      setOnboardingMessage(apiErrorMessage(e));
    } finally {
      setSendBusy(false);
    }
  }, [contractorId, loadOnboarding]);

  const modalCloseHandler = useCallback(() => {
    dispatch(closeManageApprovalModalAction());
  }, [dispatch]);

  // Memoized handlers
  const handlePayrollToggle = useCallback(() => {
    dispatch(
      alertDialogAciton({
        open: true,
        title: "Alert",
        message: `Are you sure, you wish to ${contractorDetails?.isADPCompleted ? "payroll unapprove" : "payroll approve"} this contractor?`,
        onOk: () => {
          dispatch(
            toggleAdpStatusAction({
              payload: {
                params: {
                  contractorId,
                },
              },
              successCallback: () => {
                dispatch(
                  updateManageApprovalDataAction({
                    payload: {
                      key: "isADPCompleted",
                      value: !contractorDetails?.isADPCompleted,
                    },
                  }),
                );
              },
            }),
          );
        },
      }),
    );
  }, [dispatch, contractorId, contractorDetails?.isADPCompleted]);

  const handleOnboardToggle = useCallback(() => {
    dispatch(
      alertDialogAciton({
        open: true,
        title: "Alert",
        message: `Are you sure, you wish to ${contractorDetails?.isOnBoarding ? "offboard" : "onboard"} this contractor?`,
        onOk: () => {
          dispatch(
            toggleOnboardingStatusAction({
              payload: {
                params: {
                  contractorId,
                },
              },
              successCallback: () => {
                dispatch(
                  updateManageApprovalDataAction({
                    payload: {
                      key: "isOnBoarding",
                      value: !contractorDetails?.isOnBoarding,
                    },
                  }),
                );
              },
            }),
          );
        },
      }),
    );
  }, [dispatch, contractorId, contractorDetails?.isOnBoarding]);

  const handleHRToggle = useCallback(() => {
    if (
      [
        "TBD",
        CONTRACTOR_TYPE_3RD_PARTY?.OUTSOURCED,
        CONTRACTOR_TYPE_3RD_PARTY?.THIRD_PARTY,
        undefined,
        null,
        "NA",
      ]?.includes(contractorDetails?.contractorType)
    ) {
      dispatch(
        alertDialogAciton({
          open: true,
          title: "Warning",
          message: "Please assign a contractor type before HR approval.",
        }),
      );
      return;
    }

    dispatch(
      alertDialogAciton({
        open: true,
        title: "Alert",
        message: `Are you sure, you wish to ${contractorDetails?.isGustoApproved ? "HR unapprove" : "HR approve"} this contractor?`,
        onOk: () => {
          dispatch(
            gustoApproveToggleContractorAction({ id: contractorId }, () => {
              dispatch(
                updateManageApprovalDataAction({
                  payload: {
                    key: "isGustoApproved",
                    value: !contractorDetails?.isGustoApproved,
                  },
                }),
              );
            }),
          );
        },
      }),
    );
  }, [
    dispatch,
    contractorId,
    contractorDetails?.contractorType,
    contractorDetails?.isGustoApproved,
  ]);

  const handleVerifyToggle = useCallback(() => {
    const shouldVerify = !contractorDetails?.isApproved;
    if (
      shouldVerify &&
      !hasAssignedContractorType(contractorDetails?.contractorType)
    ) {
      dispatch(
        alertDialogAciton({
          open: true,
          title: "Contractor type required",
          message: "Assign a contractor type before verifying this profile.",
        }),
      );
      return;
    }

    if (shouldVerify) {
      // Verified saves who verified and an optional note (onboarding Q1).
      setVerifyNote("");
      setNoteOpen(true);
      return;
    }

    dispatch(
      alertDialogAciton({
        open: true,
        title: "Alert",
        message:
          "Are you sure, you wish to unverify this contractor? Unverified people cannot be put on a show.",
        onOk: () => saveVerified(false),
      }),
    );
  }, [
    dispatch,
    contractorDetails?.contractorType,
    contractorDetails?.isApproved,
    saveVerified,
  ]);

  const handleEnableToggle = useCallback(() => {
    dispatch(
      alertDialogAciton({
        open: true,
        title: "Alert",
        message: `Are you sure, you wish to ${contractorDetails?.isActive ? "disable" : "enable"} this contractor profile?`,
        onOk: () => {
          dispatch(
            toggleContractorStatusAction(
              {
                id: contractorId,
                isActive: !contractorDetails?.isActive,
              },
              () => {
                dispatch(
                  updateManageApprovalDataAction({
                    payload: {
                      key: "isActive",
                      value: !contractorDetails?.isActive,
                    },
                  }),
                );
              },
            ),
          );
        },
      }),
    );
  }, [dispatch, contractorId, contractorDetails?.isActive]);

  // Static approval configuration
  const approvalList = useMemo(() => {
    return [
      {
        title: "Payroll Approval",
        key: "isADPCompleted",
        type: APPROVAL_TYPES.TOGGLE,
        onClick: handlePayrollToggle,
        readOnly: !(isUserRole(USER_ROLES.SUPERADMIN) || auth?.canManageGusto),
        disabled: Object.values(CONTRACTOR_TYPE_3RD_PARTY)?.includes(
          contractorDetails?.contractorType,
        ),
      },
      {
        title: "HR Approval",
        key: "isGustoApproved",
        isDependancy: AGREEMENT_CONTRACTOR_TYPES?.includes(
          contractorDetails?.contractorType,
        ),
        type: APPROVAL_TYPES.TOGGLE,
        haveHistory: true,
        onClick: handleHRToggle,
        readOnly: !(isUserRole(USER_ROLES.SUPERADMIN) || auth?.canManageGusto),
        disabled: Object.values(CONTRACTOR_TYPE_3RD_PARTY)?.includes(
          contractorDetails?.contractorType,
        ),
      },
      {
        title: "Onboard Contractor",
        key: "isOnBoarding",
        type: APPROVAL_TYPES.TOGGLE,
        onClick: handleOnboardToggle,
        readOnly: !(isUserRole(USER_ROLES.SUPERADMIN) || auth?.canManageGusto),
        disabled: ![CONTRACTOR_TYPE_3RD_PARTY?.THIRD_PARTY]?.includes(
          contractorDetails?.contractorType,
        ),
      },
      {
        title: "Verified",
        key: "isApproved",
        type: APPROVAL_TYPES.TOGGLE,
        onClick: handleVerifyToggle,
      },
      {
        title: "Enable Profile",
        key: "isActive",
        type: APPROVAL_TYPES.TOGGLE,
        onClick: handleEnableToggle,
      },
    ];
  }, [
    contractorDetails?.contractorType,
    handlePayrollToggle,
    handleOnboardToggle,
    handleHRToggle,
    handleVerifyToggle,
    handleEnableToggle,
    auth?.canManageGusto,
  ]);

  // Loaders map
  const loaders = useMemo(
    () => ({
      isADPCompleted: payrollLoading,
      isGustoApproved: hrLoading,
      isApproved: verifyLoading || verifyBusy,
      isActive: enableLoading,
    }),
    [payrollLoading, hrLoading, verifyLoading, verifyBusy, enableLoading],
  );

  useEffect(() => {
    if (!contractorId) return;
    dispatch(
      getContractorDetailsForManageApprovalsAction({
        payload: { contractorId },
      }),
    );
  }, [contractorId, dispatch]);

  return (
    <ManageApprovals.ModalWrapper
      open={open}
      modalCloseHandler={modalCloseHandler}
    >
      {isLoading ? <LoadingOverlay open /> : null}
      <Box
        sx={{
          display: isLoading ? "none" : "flex",
          justifyContent: "flex-start",
          flexDirection: "column",
          gap: "1.5rem",
        }}
      >
        <Typography
          fontSize="1.5rem"
          fontWeight={600}
          sx={{
            textAlign: "center",
            mb: 1,
          }}
        >
          Manage Approvals
        </Typography>

        {/* Name section */}
        <Box
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.03)",
            borderRadius: "12px",
            padding: "16px",
            border: "1px solid rgba(255, 255, 255, 0.08)",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: "500",
              color: "rgba(255, 255, 255, 0.6)",
              mb: 0.5,
            }}
          >
            Contractor Name
          </Typography>
          <Typography
            sx={{
              fontSize: "1.125rem",
              fontWeight: "500",
              color: getCssVariableHandler({
                variableName: "--primary-green-text-rgb",
              }),
            }}
          >
            {`${contractorDetails?.firstName} ${contractorDetails?.lastName}`}
            {contractorDetails?.contractorType ? (
              <Typography
                sx={{
                  paddingLeft: "1rem",
                  fontSize: "1rem",
                  fontWeight: "400",
                  color: "rgba(255, 255, 255, 0.8)",
                }}
                component={"span"}
              >
                ({contractorTypesFrontend?.[contractorDetails?.contractorType]})
              </Typography>
            ) : null}
          </Typography>
        </Box>

        {/* Approvals section */}
        <Box
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.03)",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            overflow: "hidden",
          }}
        >
          {approvalList?.map((item, index) => (
            <ApprovalItem
              key={item.key}
              title={item.title}
              valueKey={item.key}
              checked={contractorDetails?.[item.key]}
              onClick={item.onClick}
              loading={loaders[item.key]}
              isLast={index === approvalList.length - 1}
              readOnly={item?.readOnly}
              disabled={item?.disabled}
            />
          ))}
        </Box>

        {onboarding ? (
          <ManageApprovals.OnboardingPanel
            status={onboarding}
            message={onboardingMessage}
            sendBusy={sendBusy}
            onSend={handleSendOnboarding}
          />
        ) : null}
      </Box>

      <Dialog
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        fullWidth
        maxWidth="sm"
        sx={{ zIndex: 2100 }}
        PaperProps={{
          sx: { bgcolor: "#090911", color: "#fff", border: "1px solid #D175B6" },
        }}
      >
        <DialogTitle>Verify this contractor</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5, color: "rgba(255,255,255,.8)" }}>
            Verified means our team vetted this technician. Your name is saved
            with it.
          </Typography>
          <TextField
            label="Note (optional)"
            placeholder="5 years in the industry as an A1"
            value={verifyNote}
            onChange={(e) => setVerifyNote(e.target.value.slice(0, 500))}
            multiline
            minRows={2}
            fullWidth
            sx={{
              "& .MuiInputBase-root": { color: "#fff" },
              "& .MuiInputLabel-root": { color: "rgba(255,255,255,.7)" },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(209,117,182,.5)",
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setNoteOpen(false)} sx={{ color: "#fff" }}>
            Cancel
          </Button>
          <Button
            variant="text"
            disabled={verifyBusy}
            onClick={() => saveVerified(true, verifyNote.trim())}
            sx={{ color: "#D175B6", fontWeight: 600 }}
          >
            {verifyBusy ? "Saving…" : "Verify"}
          </Button>
        </DialogActions>
      </Dialog>
    </ManageApprovals.ModalWrapper>
  );
};

/**
 * Onboarding summary for the team (2026-10-08): where the person is, every step
 * (done or not, with the date when known), the worker type answers, who verified
 * and the note, and "Send onboarding" for people who said they had no offer yet (Q14).
 */
ManageApprovals.OnboardingPanel = function OnboardingPanel({
  status,
  message,
  sendBusy,
  onSend,
}) {
  const tasks = status?.tasks || [];
  const missing = tasks.filter((t) => !t.done);
  // Before the onboarding opens (no offer yet) only the profile steps apply.
  const listed = status?.onboardingOpen ? tasks : status?.profileTasks || [];
  const answers = status?.workerTypeAnswers;
  const yesNo = (v) => (v ? "Yes" : "No");
  // The ADP date is a calendar day saved as UTC midnight; show that same day everywhere.
  const stepDate = (t) =>
    new Date(t.doneAt).toLocaleDateString(
      undefined,
      t.key === "adp_finished" ? { timeZone: "UTC" } : undefined,
    );
  const stageLabel = {
    offer_question: "Has not answered the offer question yet",
    profile_only: "Said they have no offer yet (profile only)",
    onboarding: "Onboarding in progress",
    complete: "Onboarding complete",
  }[status?.stage];
  const canSend = !status?.onboardingOpen || status?.offerAnswer === "no";
  return (
    <Box
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        borderRadius: "12px",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
      }}
    >
      <Typography sx={{ fontWeight: 600 }}>Onboarding</Typography>
      <Typography sx={{ color: "rgba(255,255,255,.8)", fontSize: "0.95rem" }}>
        {stageLabel}
        {status?.onboardingOpen
          ? ` · ${tasks.length - missing.length}/${tasks.length} steps`
          : ""}
      </Typography>
      {listed.length ? (
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
          {listed.map((t) => (
            <Box
              component="li"
              key={t.key}
              sx={{ display: "flex", alignItems: "baseline", gap: "0.5rem", py: "2px" }}
            >
              <Typography
                component="span"
                aria-label={t.done ? "Done" : "Not done"}
                sx={{ color: t.done ? "#00EEC5" : "#ff6b6b", fontSize: "0.9rem", width: "1rem" }}
              >
                {t.done ? "✓" : "✗"}
              </Typography>
              <Typography
                component="span"
                sx={{ color: t.done ? "rgba(255,255,255,.85)" : "rgba(255,255,255,.6)", fontSize: "0.85rem", flex: 1 }}
              >
                {t.label}
                {t.key === "worker_type" && status?.workerType
                  ? `: ${status.workerType === "w2" ? "W2" : status.workerType}`
                  : ""}
              </Typography>
              {t.doneAt ? (
                <Typography component="span" sx={{ color: "rgba(255,255,255,.5)", fontSize: "0.8rem" }}>
                  {stepDate(t)}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Box>
      ) : null}
      {answers ? (
        <Typography sx={{ color: "rgba(255,255,255,.65)", fontSize: "0.85rem" }}>
          Answers: California {yesNo(answers.isCalifornia)} · EIN {yesNo(answers.hasEin)} · COI{" "}
          {yesNo(answers.hasCoi)}
        </Typography>
      ) : null}
      {status?.verified ? (
        <Typography sx={{ color: "rgba(255,255,255,.8)", fontSize: "0.9rem" }}>
          Verified
          {status?.verifiedBy ? ` by ${status.verifiedBy}` : ""}
          {status?.verifiedOn
            ? ` on ${new Date(status.verifiedOn).toLocaleDateString()}`
            : ""}
          {status?.verifyNote ? ` · "${status.verifyNote}"` : ""}
        </Typography>
      ) : null}
      {status?.onboardingSentAt ? (
        <Typography sx={{ color: "rgba(255,255,255,.65)", fontSize: "0.85rem" }}>
          Onboarding sent on {new Date(status.onboardingSentAt).toLocaleDateString()}
          {status?.onboardingSentBy ? ` by ${status.onboardingSentBy}` : ""}
        </Typography>
      ) : null}
      {canSend ? (
        <Button
          className="BtnBlackPink"
          disabled={sendBusy}
          onClick={onSend}
          sx={{ alignSelf: "flex-start", mt: 0.5 }}
        >
          {sendBusy
            ? "Sending…"
            : status?.onboardingSentAt
              ? "Send onboarding again"
              : "Send onboarding"}
        </Button>
      ) : null}
      {message ? (
        <Typography sx={{ color: "#00EEC5", fontSize: "0.85rem" }}>{message}</Typography>
      ) : null}
    </Box>
  );
};

ManageApprovals.OnboardingPanel.propTypes = {
  status: PropTypes.object,
  message: PropTypes.string,
  sendBusy: PropTypes.bool,
  onSend: PropTypes.func,
};

ManageApprovals.ModalWrapper = function ModalWrapper({
  open,
  modalCloseHandler,
  children,
}) {
  return (
    <Modal open={open} onClose={modalCloseHandler} sx={{ zIndex: 2000 }}>
      <Box sx={{ ...style, position: "relative" }}>
        <CustomIconButtonForSheet
          sx={{
            backgroundColor: "transparent !important",
            flexShrink: 0,
            position: "absolute",
            top: "16px",
            right: "16px",
            zIndex: 10,
          }}
          onClick={modalCloseHandler}
        >
          <Close />
        </CustomIconButtonForSheet>

        {children}
      </Box>
    </Modal>
  );
};

ManageApprovals.ModalWrapper.propTypes = {
  open: PropTypes.bool,
  modalCloseHandler: PropTypes.func,
  children: PropTypes.any,
};
